/**
 * @author Sarah Nouh
 * @copyright Copyright 2020 by Radivision Inc., CA, USA. All Rights Reserved.
 * @Date: 2018-07-31 02:33
 * @description Implementation of the search filters.
 * @filename search-filters.tsx
 */
import React from "react";
import { FilterContent } from "../../component-configuration/filter-content";
import { SVG } from "../../component-configuration/svgs";

/**
 * The props of the SearchFilters component.
 *
 * @interface
 */
interface SearchFiltersProps {
  /**
   * The collection of filters.
   *
   * @type {Array<FilterContent>}
   */
  filters: FilterContent[];

  /**
   * update list with filters
   *
   * @type {Function}
   */
  updateFilters: Function;
}

/**
 * A React component that renders the search filters.
 *
 * @export
 * @class SearchFilters
 * @extends {React.Component<SearchProps>}
 */
export class SearchFilters extends React.Component<
  SearchFiltersProps,
  { filtersCount: number }
> {
  /**
   * The selected check boxes.
   *
   * @type {Array<{checkboxId:name, filterId:number, label:name}>}
   */
  private selectedCheckboxes: {
    /**
     * The ID of the checkbox.
     *
     * @type {number}
     */
    checkboxId: number;

    /**
     * The ID of the filter.
     *
     * @type {number}
     */
    filterId: number;

    /**
     * The label of the filter.
     *
     * @type {string}
     */
    label: string;
  }[];

  /**
   * Constructor.
   *
   * @param {SearchFiltersProps} props The props of the component.
   */
  constructor(props: SearchFiltersProps) {
    super(props);
    this.state = {
      filtersCount: 0,
    };
    this.selectedCheckboxes = [];
  }

  /**
   * A function to clear all checkboxes.
   *
   * @type {function()}
   */
  handleClearAll: () => void = () => {
    // empty the array -> all the tags will be removed
    this.selectedCheckboxes = [];
    // loop on checkboxes and uncheck all of them
    for (let i = 0; i < this.props.filters.length; i++) {
      // clearing all checkboxes counts from all filters
      this.props.filters[i].checkedCount = 0;
      // unchecking all the checkboxes
      for (let j = 0; j < this.props.filters[i].checkboxes.length; j++) {
        this.props.filters[i].checkboxes[j].checked = false;
      }
    }
    // reset the state to re-render after the updates
    this.setState({
      filtersCount: 0,
    });
    this.props.updateFilters();
  };

  /**
   * A function to know which checkboxes are checked at any given moment in time. If the label in the set - it's checked; if
   * not - it's unchecked.
   *
   * @type {function(label: string, currentFilterIndex: number, currentCheckboxIndex: number)}
   */
  handleCheck: (
    label: string,
    currentFilterIndex: number,
    currentCheckboxIndex: number
  ) => void = (
    label: string,
    currentFilterIndex: number,
    currentCheckboxIndex: number
  ) => {
    const currentFilter = this.props.filters[currentFilterIndex];
    const currentCheckbox = currentFilter.checkboxes[currentCheckboxIndex];
    const currentState = currentCheckbox.checked;
    let index: number;
    let newCheckbox: {
      filterId: number;
      checkboxId: number;
      label: string;
    };

    currentCheckbox.checked = !currentState;
    if (this.selectedCheckboxes.find((l) => l.label === label)) {
      // the label is found then the checkbox was selected (so now it's being unchecked)-> remove it from the array to remove the tag
      index = this.selectedCheckboxes.findIndex((l) => l.label === label);
      this.selectedCheckboxes.splice(index, 1);
      currentFilter.checkedCount--;
      // updating the state to re-render the updates
      this.setState({
        filtersCount: this.state.filtersCount - 1,
      });
    } else {
      // the label was not found -> the checkbox was unchecked (now it's being checked) -> add it to the array to add the tag to the view
      newCheckbox = {
        label,
        filterId: currentFilterIndex,
        checkboxId: currentCheckboxIndex,
      };
      this.selectedCheckboxes.push(newCheckbox);
      currentFilter.checkedCount++;
      // updating the state to re-render the updates
      this.setState({
        filtersCount: this.state.filtersCount + 1,
      });
    }
    this.props.updateFilters();
  };

  /**
   * A function to to clear the tag and it's corresponding filter.
   *
   * @type {function(tag: string)}
   */
  handleRemoveFilter(tag: string) {
    let index: number;

    // function called when the close icon in the tag is clicked (should remove the tag and uncheck the corresponding checkbox)
    this.setState({
      filtersCount: this.state.filtersCount - 1,
    });

    const currentCheckbox = this.selectedCheckboxes.find(
      (cb) => cb.label === tag
    );

    const currentFilterIndex = currentCheckbox.filterId;
    const currentFilter = this.props.filters[currentFilterIndex];
    const currentState =
      currentFilter.checkboxes[currentCheckbox.checkboxId].checked;
    // get the checkbox and reverse it's state
    currentFilter.checkboxes[
      currentCheckbox.checkboxId
    ].checked = !currentState;
    // reduce the filters count by 1
    currentFilter.checkedCount--;
    // remove the label from the array to remove the tag from the view
    index = this.selectedCheckboxes.findIndex((l) => l.label === tag);
    this.selectedCheckboxes.splice(index, 1);
    this.props.updateFilters();
  }
  /**
   * A function to to clear checkboxes within a filter.
   *
   * @type {function(filterIndex: number)}
   */
  handleFilterClear = (filterIndex: number) => {
    // function triggered when clicking 'clear all' inside a filter
    const currentFilter = this.props.filters[filterIndex];
    for (let i = 0; i < currentFilter.checkboxes.length; i++) {
      currentFilter.checkboxes[i].checked = false;
      if (this.selectedCheckboxes.find((l) => l.filterId === filterIndex)) {
        // the label is found then the checkbox was selected (so now it's being unchecked)-> remove it from the array to remove the tag
        const index = this.selectedCheckboxes.findIndex(
          (l) => l.filterId === filterIndex
        );
        this.selectedCheckboxes.splice(index, 1);
      }
    }
    // clear the filters count
    currentFilter.checkedCount = 0;
    this.setState({
      filtersCount: this.state.filtersCount - currentFilter.checkboxes.length,
    });
    this.props.updateFilters();
  };
  /**
   * Returns a ReactNode containing the rendered component.
   *
   * @returns {React.ReactNode} The ReactNode containing the rendered component.
   */
  render(): React.ReactNode {
    return (
      <div className=" row search-filters">
        <div className="col-xs-12 col-sm-8 col-md-12 col-lg-9">
          <div className="filters">
            <h6 className="title">Filter By:</h6>
            {this.props.filters.map((filter, index) => {
              let columnsClass = "";
              filter.filterName === "Industry"
                ? (columnsClass = "wide-dropdown-menu")
                : (columnsClass = "");

              return (
                <div className="dropdown" key={index.toString()}>
                  <button
                    className="btn dropdown-button dropdown-toggle"
                    type="button"
                    id="dropdownMenuButton"
                    data-toggle="dropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                  >
                    {filter.filterName}
                    {filter.checkedCount > 0 ? ` (${filter.checkedCount})` : ""}
                  </button>
                  <div
                    className={`dropdown-menu ${columnsClass}`}
                    aria-labelledby="dropdownMenuButton"
                  >
                    {filter.checkboxes.map((checkbox, innerIndex) => {
                      return (
                        <div
                          className="dropdown-item"
                          key={innerIndex.toString()}
                        >
                          <input
                            className="checkbox"
                            id={checkbox.id}
                            type="checkbox"
                            value={checkbox.label}
                            checked={checkbox.checked}
                            onChange={() => {
                              this.handleCheck(
                                checkbox.label,
                                index,
                                innerIndex
                              );
                            }}
                          />
                          <label htmlFor={checkbox.id}>
                            <span>{checkbox.label}</span>
                          </label>
                        </div>
                      );
                    })}
                    <div
                      className="clear-filter"
                      onClick={() => {
                        this.handleFilterClear(index);
                      }}
                    >
                      {SVG.crosshairs}
                      Clear All
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="col-xs-12 col-sm-12 col-md-12 col-lg-12 for_tags">
          <div className="tags">
            {this.selectedCheckboxes.map((tag, index) => {
              return (
                <span className="tag" key={index.toString()}>
                  {tag.label}
                  <i onClick={() => this.handleRemoveFilter(tag.label)}>{SVG.crosshairs}</i>
                </span>
              );
            })}
            {this.selectedCheckboxes.length > 0 ? (
              <span
                className="tag clear"
                onClick={() => {
                  this.handleClearAll();
                }}
              >
                clear all filters
                {SVG.crosshairs}
              </span>
            ) : (
              <span />
            )}
          </div>
        </div>
        {/* <div className="col-sm-4 col-lg-3">
          <h6 className="title">Sort by:</h6>
          <div className="dropdown trending">
            <button
              className="btn dropdown-button dropdown-toggle"
              type="button"
              id="dropdownMenuButton"
              data-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
            >
              Trending
            </button>
            <div className="dropdown-menu" aria-labelledby="dropdownMenuButton">
              <div className="dropdown-item">
                <input
                  className="checkbox"
                  id="checkbox-1"
                  type="checkbox"
                  value="value1"
                />
                <label htmlFor="checkbox-1">Trending</label>
              </div>
              <div className="dropdown-item">
                <input
                  className="checkbox"
                  id="checkbox-2"
                  type="checkbox"
                  value="value2"
                />
                <label htmlFor="checkbox-2">most popular</label>
              </div>
              <div className="dropdown-item">
                <input
                  className="checkbox"
                  id="checkbox-3"
                  type="checkbox"
                  value="value3"
                />
                <label htmlFor="checkbox-3">newest to oldest</label>
              </div>
              <div className="dropdown-item">
                <input
                  className="checkbox"
                  id="checkbox-4"
                  type="checkbox"
                  value="value4"
                />
                <label htmlFor="checkbox-4">a-z</label>
              </div>
              <div className="dropdown-item">
                <input
                  className="checkbox"
                  id="checkbox-5"
                  type="checkbox"
                  value="value5"
                />
                <label htmlFor="checkbox-5"># of employees</label>
              </div>
            </div>
          </div>
        </div> */}
      </div>
    );
  }
}
